/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: inherit
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Modal } from '../../../shared/modals/Modal';
import {
  Platform,
  Pressable,
  ScrollView,
  Text,
  TouchableOpacity,
  useWindowDimensions,
  View,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { colors } from '../../../shared/theme/colors'
import { spacing } from '../../../shared/theme/spacing';
import { normalizeProductCategoryId } from '../../../catalog/domain-api'
import type { CatalogItemTypeFilter } from '../../../catalog/domain-api';
import { categoryAppliesToShortLabel } from '../../../catalog/domain-api'
import type { CategoryFilterOption } from '../../../catalog/domain-api';
import type { ReportsScreenViewModel } from '../useReportsScreen';
import {
  getReportsVentasLayoutMode,
  REPORTS_VENTAS_WIDE_BREAKPOINT,
  type ReportsVentasLayoutMode,
} from '../reportsVentasLayout';
import { styles } from '../ReportsScreen.styles';
import { MasterFilterCompactPicker } from '../../../shared/filters/MasterFilterCompactPicker'
import { MasterFilterSheetModal } from '../../../shared/filters/MasterFilterSheetModal';
import { operationalFilterStyles as opStyles } from '../../../shared/filters/operationalFilters.styles';
import { ticketColors } from '../../../shared/theme/ticketColors';

const CATEGORY_MODAL_PAGE_SIZE = 42;

const ITEM_TYPE_OPTIONS: Array<{ id: CatalogItemTypeFilter; label: string }> = [
  { id: 'all', label: 'Todos' },
  { id: 'physical', label: 'Productos' },
  { id: 'service', label: 'Servicios' },
];

/**
 * Tipo + categoría como en Catálogo: control compacto + bottom sheet (lista con buscador implícito: scroll).
 */
export function ReportsSalesListFilterPickers({
  vm,
  disabled,
  layout: layoutProp,
  embedInGridRow = false,
}: {
  vm: ReportsScreenViewModel;
  disabled: boolean;
  layout?: ReportsVentasLayoutMode;
  /** En escritorio: los selectores se renderizan como hijos de la rejilla compartida (con operador/cliente). */
  embedInGridRow?: boolean;
}) {
  const { width: windowWidth } = useWindowDimensions();
  const layout = layoutProp ?? getReportsVentasLayoutMode(windowWidth);
  const { layoutWide } = layout;
  const pickerRowOuterStyle = { alignSelf: 'stretch' as const, width: '100%' as const };
  const pickerRowStylePatch = layoutWide ? styles.reportsSalesListPickerRowWide : undefined;

  const insets = useSafeAreaInsets();
  const [tipoOpen, setTipoOpen] = useState(false);
  const [catOpen, setCatOpen] = useState(false);
  const [brandSheetOpen, setBrandSheetOpen] = useState(false);
  const [supplierSheetOpen, setSupplierSheetOpen] = useState(false);
  const [categoryListPage, setCategoryListPage] = useState(0);

  const isWebWideModal = Platform.OS === 'web' && windowWidth >= REPORTS_VENTAS_WIDE_BREAKPOINT;

  const tipoButtonLabel = ITEM_TYPE_OPTIONS.find((o) => o.id === vm.salesByProductItemTypeFilter)?.label ?? 'Todos';

  const categoryButtonLabel = useMemo(() => {
    if (!vm.salesByProductCategory) return 'Todas';
    const slug = normalizeProductCategoryId(vm.salesByProductCategory);
    const opt = vm.salesByProductCategoryOptions.find(
      (c) => c.id !== 'all' && normalizeProductCategoryId(String(c.id)) === slug
    );
    return opt?.label ?? vm.salesByProductCategory;
  }, [vm.salesByProductCategory, vm.salesByProductCategoryOptions]);

  const tipoCustom = vm.salesByProductItemTypeFilter !== 'all';
  const categoryCustom = vm.salesByProductCategory != null;

  const brandButtonLabel = useMemo(() => {
    if (!vm.reportsVentasBrandFilter) return 'Todas';
    const m = vm.reportsVentasBrandOptions.find((b) => b.id === vm.reportsVentasBrandFilter);
    return m?.name ?? 'Marca';
  }, [vm.reportsVentasBrandFilter, vm.reportsVentasBrandOptions]);

  const supplierButtonLabel = useMemo(() => {
    if (!vm.reportsVentasSupplierFilter) return 'Todos';
    return vm.reportsVentasSupplierOptions.find((s) => s.id === vm.reportsVentasSupplierFilter)?.name ?? 'Proveedor';
  }, [vm.reportsVentasSupplierFilter, vm.reportsVentasSupplierOptions]);

  const brandCustom = vm.reportsVentasBrandFilter != null;
  const supplierCustom = vm.reportsVentasSupplierFilter != null;

  const categoryOptsAllIdx = vm.salesByProductCategoryOptions;
  const categoryAllRow = useMemo(
    () => categoryOptsAllIdx.find((c) => c.id === 'all'),
    [categoryOptsAllIdx]
  );
  const categoryOptsRest = useMemo(
    () => categoryOptsAllIdx.filter((c) => c.id !== 'all'),
    [categoryOptsAllIdx]
  );
  const categoryPageCount = Math.max(1, Math.ceil(categoryOptsRest.length / CATEGORY_MODAL_PAGE_SIZE));
  const categorySlice = useMemo(() => {
    const start = categoryListPage * CATEGORY_MODAL_PAGE_SIZE;
    return categoryOptsRest.slice(start, start + CATEGORY_MODAL_PAGE_SIZE);
  }, [categoryOptsRest, categoryListPage]);

  useEffect(() => {
    if (catOpen) {
      setCategoryListPage(0);
    }
  }, [catOpen]);

  useEffect(() => {
    if (categoryListPage > categoryPageCount - 1) {
      setCategoryListPage(categoryPageCount - 1);
    }
  }, [categoryListPage, categoryPageCount]);

  const sheetBottomPad = Math.max(insets.bottom, spacing.md);

  const renderCategoryOption = (filter: CategoryFilterOption) => {
    const slug = vm.salesByProductCategory ? normalizeProductCategoryId(vm.salesByProductCategory) : null;
    const selected =
      filter.id === 'all'
        ? !vm.salesByProductCategory
        : slug != null && normalizeProductCategoryId(String(filter.id)) === slug;
    const scopeLabel =
      filter.id !== 'all' && filter.appliesTo ? categoryAppliesToShortLabel(filter.appliesTo) : null;
    return (
      <TouchableOpacity
        key={String(filter.id)}
        style={[styles.reportsSalesListSheetRow, selected && styles.reportsSalesListSheetRowSelected]}
        onPress={() => {
          if (filter.id === 'all') {
            vm.setSalesByProductCategory(null);
          } else {
            vm.setSalesByProductCategory(String(filter.id));
          }
          setCatOpen(false);
        }}
        disabled={disabled}
        accessibilityRole="button"
        accessibilityState={{ selected }}
        accessibilityLabel={scopeLabel ? `${filter.label}, ${scopeLabel}` : filter.label}
      >
        <View style={styles.reportsSalesListSheetRowText}>
          <Text style={[styles.reportsSalesListSheetRowTitle, selected && styles.reportsSalesListSheetRowTitleSelected]}>
            {filter.label}
          </Text>
          {scopeLabel ? <Text style={styles.reportsSalesListSheetRowSubtitle}>{scopeLabel}</Text> : null}
        </View>
        {selected ? <Ionicons name="checkmark-circle" size={22} color={ticketColors.ok} /> : null}
      </TouchableOpacity>
    );
  };

  const pickerWrap = (node: React.ReactNode) => (
    <View style={layoutWide ? opStyles.wideToolbarGroup : opStyles.mobilePickerGridItem}>{node}</View>
  );

  const pickerControls = (
    <>
      {pickerWrap(
        <MasterFilterCompactPicker
          caption="Tipo"
          value={tipoButtonLabel}
          active={tipoCustom}
          dense={!layoutWide}
          onPress={() => !disabled && setTipoOpen(true)}
        />
      )}
      {pickerWrap(
        <MasterFilterCompactPicker
          caption="Categoría"
          value={categoryButtonLabel}
          active={categoryCustom}
          dense={!layoutWide}
          onPress={() => !disabled && setCatOpen(true)}
        />
      )}
      {vm.reportsVentasBrandOptions.length > 0
        ? pickerWrap(
            <MasterFilterCompactPicker
              caption="Marca"
              value={brandButtonLabel}
              active={brandCustom}
              dense={!layoutWide}
              onPress={() => !disabled && setBrandSheetOpen(true)}
            />
          )
        : null}
      {vm.reportsVentasSupplierOptions.length > 0
        ? pickerWrap(
            <MasterFilterCompactPicker
              caption="Proveedor"
              value={supplierButtonLabel}
              active={supplierCustom}
              dense={!layoutWide}
              onPress={() => !disabled && setSupplierSheetOpen(true)}
            />
          )
        : null}
    </>
  );

  const pickerRow = layoutWide ? (
    <View style={pickerRowOuterStyle}>
      <View style={[opStyles.wideToolbarRow, pickerRowStylePatch]}>{pickerControls}</View>
    </View>
  ) : (
    pickerControls
  );

  return (
    <>
      {pickerRow}

      <MasterFilterSheetModal
        visible={tipoOpen}
        title="Tipo"
        options={ITEM_TYPE_OPTIONS.map((o) => ({ id: o.id, name: o.label }))}
        selectedId={vm.salesByProductItemTypeFilter}
        onSelect={(id) => {
          if (id) vm.setSalesByProductItemTypeFilter(id as CatalogItemTypeFilter);
        }}
        onClose={() => setTipoOpen(false)}
        showAllOption={false}
      />

      <Modal visible={catOpen} transparent animationType="fade" onRequestClose={() => setCatOpen(false)}>
        <Pressable
          style={[styles.reportsSalesListModalBackdrop, isWebWideModal && styles.reportsSalesListModalBackdropWeb]}
          onPress={() => setCatOpen(false)}
        >
          <Pressable
            style={[
              styles.reportsSalesListModalSheet,
              isWebWideModal && styles.reportsSalesListModalSheetWeb,
              { paddingBottom: sheetBottomPad },
            ]}
            onPress={(e) => e.stopPropagation()}
          >
            <View style={styles.reportsSalesListModalHeader}>
              <Text style={styles.reportsSalesListModalTitle}>Categoría</Text>
              <TouchableOpacity onPress={() => setCatOpen(false)} accessibilityRole="button" accessibilityLabel="Cerrar" hitSlop={12}>
                <Ionicons name="close" size={26} color={ticketColors.ink} />
              </TouchableOpacity>
            </View>
            <ScrollView
              style={[styles.reportsSalesListModalScroll, isWebWideModal && { maxHeight: 460 }]}
              keyboardShouldPersistTaps="handled"
            >
              {categoryAllRow ? renderCategoryOption(categoryAllRow) : null}
              {categorySlice.map((o) => renderCategoryOption(o))}
            </ScrollView>
            {categoryOptsRest.length > CATEGORY_MODAL_PAGE_SIZE ? (
              <View
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  paddingTop: spacing.sm,
                  gap: spacing.sm,
                }}
              >
                <TouchableOpacity
                  accessibilityRole="button"
                  accessibilityLabel="Página anterior de categorías"
                  disabled={disabled || categoryListPage <= 0}
                  onPress={() => setCategoryListPage((p) => Math.max(0, p - 1))}
                  style={[styles.secondaryButton, (disabled || categoryListPage <= 0) && styles.disabled]}
                >
                  <Text style={[styles.secondaryButtonText, (disabled || categoryListPage <= 0) ? { opacity: 0.5 } : null]}>
                    Anterior
                  </Text>
                </TouchableOpacity>
                <Text style={[styles.caption, { flex: 1, textAlign: 'center', color: ticketColors.ink3 }]}>
                  Página {categoryListPage + 1} / {categoryPageCount}
                </Text>
                <TouchableOpacity
                  accessibilityRole="button"
                  accessibilityLabel="Página siguiente de categorías"
                  disabled={disabled || categoryListPage >= categoryPageCount - 1}
                  onPress={() => setCategoryListPage((p) => Math.min(categoryPageCount - 1, p + 1))}
                  style={[
                    styles.secondaryButton,
                    (disabled || categoryListPage >= categoryPageCount - 1) && styles.disabled,
                  ]}
                >
                  <Text
                    style={[
                      styles.secondaryButtonText,
                      (disabled || categoryListPage >= categoryPageCount - 1) ? { opacity: 0.5 } : null,
                    ]}
                  >
                    Siguiente
                  </Text>
                </TouchableOpacity>
              </View>
            ) : null}
          </Pressable>
        </Pressable>
      </Modal>

      <MasterFilterSheetModal
        visible={brandSheetOpen}
        title="Marca"
        options={vm.reportsVentasBrandOptions}
        selectedId={vm.reportsVentasBrandFilter}
        onSelect={(id) => vm.setReportsVentasBrandFilter(id)}
        onClose={() => setBrandSheetOpen(false)}
        allOptionLabel="Todas"
      />
      <MasterFilterSheetModal
        visible={supplierSheetOpen}
        title="Proveedor"
        options={vm.reportsVentasSupplierOptions}
        selectedId={vm.reportsVentasSupplierFilter}
        onSelect={(id) => vm.setReportsVentasSupplierFilter(id)}
        onClose={() => setSupplierSheetOpen(false)}
        allOptionLabel="Todos"
      />
    </>
  );
}
